import { Select, Space } from "antd";
import { debounce, groupBy } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { StyledTable as Table } from "../../Common/Components/StyledTable";
import { StyledLink } from "../../Common/Components/index";
import { PaginationComponent } from "~Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { PermissionNames } from "~enums/permissions";
import { useGetOrgData } from "~hooks/useGetOrgData";
import { useHandlePagination } from "~hooks/useHandlePagination";
import usePermissionsByCompetitionName from "../../hooks/usePermissionsByCompetitionName";
import useGetCurrentUserPermissionScope from "../../hooks/useGetCurrentUserPermissionScope";

const getHeaderCell = (testId) => ({
  "data-testid": `table-header-cell-${testId}`,
});
const getCell = (testId) => ({"data-testid": `table-cell-${testId}`});

const columns = ({
  customerId,
  sport,
  organizationId,
  formatMessage,
  competitions,
  filteredSeasons,
  seasonEntityPermissionName,
}) => {
  return [
    {
      title: formatMessage({
        id: "year",
        defaultMessage: "Year",
      }),
      dataIndex: "year",
      onHeaderCell: (column) => getHeaderCell("year"),
      onCell: (record) => getCell(`${record.seasonId}-year`),
      defaultSortOrder: "descend",
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
    },
    {
      title: formatMessage({
        id: "competitionname",
        defaultMessage: "Competition Name",
      }),
      dataIndex: "competitionId",
      onHeaderCell: (column) => getHeaderCell("competitionname"),
      onCell: (record) => getCell(`${record.seasonId}-competitionname`),
      render: (competitionId) => competitions?.[competitionId]?.nameLocal,
      filters: Object.keys(groupBy(filteredSeasons, "competitionId"))
        ?.map(
          (x) =>
            ({
              text: competitions?.[x]?.nameLocal,
              value: x,
            }) || [],
        )
        .sort(),
      onFilter: (value, record) => value === record.competitionId,
      filterSearch: true,
    },
    {
      title: formatMessage({
        id: "season.title",
        defaultMessage: "Season Title",
      }),
      dataIndex: "nameLocal",
      render: (nameLocal, record) => <span style={{color: "rgba(0, 0, 0, 0.85)"}}>{nameLocal}</span>,
      onHeaderCell: (column) => getHeaderCell("nameLocal"),
      onCell: (record) => getCell(`${record.seasonId}-nameLocal`),
    },
    {
      title: formatMessage({
        id: "gender",
        defaultMessage: "Gender",
      }),
      dataIndex: "gender",
      onHeaderCell: (column) => getHeaderCell("gender"),
      onCell: (record) => getCell(`${record.seasonId}-gender`),
      filters: Object.keys(groupBy(filteredSeasons, "gender"))
        ?.map(
          (x) =>
            ({
              text: x,
              value: x,
            }) || [],
        )
        .sort(),
      onFilter: (value, record) => value === record.gender,
      filterSearch: true,
    },
    {
      title: formatMessage({
        id: "ageGroup",
        defaultMessage: "Age Group",
      }),
      dataIndex: "ageGroup",
      onHeaderCell: (column) => getHeaderCell("ageGroup"),
      onCell: (record) => getCell(`${record.seasonId}-ageGroup`),
      filters: Object.keys(groupBy(filteredSeasons, "ageGroup"))
        ?.map(
          (x) =>
            ({
              text: x,
              value: x,
            }) || [],
        )
        .sort(),
      onFilter: (value, record) => value === record.ageGroup,
      filterSearch: true,
    },
    {
      render: (_, record) => (
        <PermissionCheck permissionKeys={[PermissionNames.FIXTURE]}>
          <StyledLink
            to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/fixtures`}
            onClick={(e) => e.stopPropagation()}
          >
            {formatMessage({
              id: "fixtures",
              defaultMessage: "Fixtures",
            })}
          </StyledLink>
        </PermissionCheck>
      ),
      onCell: (record) => getCell(`${record.seasonId}-fixtures-link`),
      width: 1,
    },
    {
      render: (_, record) => (
        <StyledLink
          to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/edit`}
          onClick={(e) => e.stopPropagation()}
        >
          {formatMessage({
            id: "details",
            defaultMessage: "Details",
          })}
        </StyledLink>
      ),
      onCell: (record) => getCell(`${record.seasonId}-details-link`),
      width: 1,
    },
    {
      render: (_, record) => (
        <PermissionCheck permissionKeys={[seasonEntityPermissionName]}>
          <StyledLink
            to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/teams`}
            onClick={(e) => e.stopPropagation()}
          >
            {formatMessage({
              id: "teams",
              defaultMessage: "Teams",
            })}
          </StyledLink>
        </PermissionCheck>
      ),
      onCell: (record) => getCell(`${record.seasonId}-teams-link`),
      width: 1,
    },
    {
      render: (_, record) => (
        <PermissionCheck permissionKeys={[PermissionNames.STANDING]}>
          <StyledLink
            to={`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/standings`}
            onClick={(e) => e.stopPropagation()}
          >
            {formatMessage({
              id: "standings",
              defaultMessage: "Standings",
            })}
          </StyledLink>
        </PermissionCheck>
      ),
      onCell: (record) => getCell(`${record.seasonId}-standings-link`),
      width: 1,
    },
  ];
};

const SeasonsList = () => {
  const {formatMessage} = useIntl();
  const [activeFilter, setActiveFilter] = useState("ACTIVE");
  const {sport, organizationId, customerId} = useGetOrgData();
  const [searchText, setSearchText] = useState("");
  const { data: scopeData, isLoading } = useGetCurrentUserPermissionScope();
  const { scope, resources } = scopeData || {};

  const allSeasons = useMemo(
    () => {
      const seasonResources = resources?.seasons || {};
      const seasons = [];

      scope?.leagueTree?.forEach(({ competitions }) => competitions.forEach(({ seasonIds }) => {
        seasons.push(...seasonIds.map((seasonId) => seasonResources[seasonId]));
      }));

      return seasons;
    },
    [scope?.leagueTree, resources?.seasons],
  );

  const filteredSeasons = useMemo(
    () => allSeasons.filter(({ status, nameLocal }) => {
      if (activeFilter !== "ALL" && status !== activeFilter) {
        return false;
      }
      if (searchText && !nameLocal?.toLowerCase()?.includes(searchText.toLowerCase())) {
        return false;
      }
      return true;
    }),
    [allSeasons, activeFilter, searchText],
  );

  const {
    pageSize,
    currentPage,
    handlePaginationChange,
    pageSizeOptions
  } = useHandlePagination(50, 1, [50, 100, 500, 1000]);

  const changeActiveFilter = (value) => {
    setActiveFilter(value);
  };

  const navigate = useNavigate();
  const {seasonEntityPermissionName} = usePermissionsByCompetitionName();

  const handleChange = useCallback(
    debounce((value) => {
      if (value.length > 1) {
        setSearchText(value);
      } else {
        setSearchText("");
      }
    }, 1000),
    [],
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className={"table-title"}
      >
        Seasons
        <Space>
          <Select
            data-testid={`season-status-filter`}
            value={activeFilter}
            onChange={changeActiveFilter}
            variant="borderless"
          >
            <Select.Option
              disabled={activeFilter === "ALL"}
              value={"ALL"}
              data-testid={`season-status-filter-option-all`}
            >
              <strong>All Seasons</strong>
            </Select.Option>
            <Select.Option
              disabled={activeFilter === "ACTIVE"}
              value={"ACTIVE"}
              data-testid={`season-status-filter-option-active`}
            >
              <strong>Active Seasons</strong>
            </Select.Option>
            <Select.Option
              disabled={activeFilter === "DRAFT"}
              value={"DRAFT"}
              data-testid={`season-status-filter-option-draft`}
            >
              <strong>Draft Seasons</strong>
            </Select.Option>
            <Select.Option
              disabled={activeFilter === "PENDING"}
              value={"PENDING"}
              data-testid={`season-status-filter-option-pending`}
            >
              <strong>Pending Seasons</strong>
            </Select.Option>
            <Select.Option
              disabled={activeFilter === "INACTIVE"}
              value={"INACTIVE"}
              data-testid={`season-status-filter-option-inactive`}
            >
              <strong>Inactive Seasons</strong>
            </Select.Option>
          </Select>
          <SearchFilter placeholder="Search (2 letters min)" doSearch={handleChange}/>
        </Space>
      </div>

      <Table
        loading={isLoading}
        size={"small"}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {
              navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${record?.seasonId}/fixtures`);
            },
            "data-testid": `table-row-${record?.seasonId}`,
          };
        }}
        onHeaderRow={(columns, index) => {
          return {
            "data-testid": `table-header`,
          };
        }}
        columns={columns({
          customerId,
          sport,
          organizationId,
          formatMessage,
          competitions: resources?.competitions,
          filteredSeasons,
          seasonEntityPermissionName,
        })}
        rowKey={(record) => record?.seasonId}
        dataSource={filteredSeasons}
        pagination={{
          current: currentPage,
          defaultPageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: pageSizeOptions,
          showQuickJumper: false,
          total: filteredSeasons?.length || 0,
          position: ["bottomCenter"],
          onChange: handlePaginationChange,
          showTotal: (total, range) => (
            <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range}/>
          ),
        }}
        data-testid={`seasons-table`}
      />
    </>
  );
};

export default SeasonsList;
